import { FormSectionComponent } from "@/components/forms/components/FormSectionComponent.tsx";
import { HighlightDroppable } from "@/components/forms/components/HighlightDroppable.tsx";
import { useFormEditor } from "@/components/forms/form-editor-context.ts";
import { useFormElementUtils } from "@/components/forms/utils/useFormElementUtils.tsx";
import { Stack, Title } from "@skailer-com/uikit";
import { Draggable } from "@hello-pangea/dnd";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface FormPageComponentProps {
  pageIndex: number;
}

export const FormPageComponent = ({ pageIndex }: FormPageComponentProps) => {
  const { t } = useTranslation();
  const { formValues, readOnly } = useFormEditor();
  const { renderEditBlock } = useFormElementUtils();

  const page = formValues.pages[pageIndex];
  if (formValues.pages.length <= 1) {
    return (
      <HighlightDroppable droppableId={`sections-${pageIndex}`} type="section">
        {(provided) => (
          <Stack gap={"lg"} {...provided.droppableProps} ref={provided.innerRef}>
            {page.sections.map((_, sectionIndex) => (
              <Fragment key={`section-${pageIndex}-${sectionIndex}`}>
                <FormSectionComponent pageIndex={pageIndex} sectionIndex={sectionIndex} />
                {page.sections.length > 1 && <hr className="ml-34" />}
              </Fragment>
            ))}
            {provided.placeholder}
          </Stack>
        )}
      </HighlightDroppable>
    );
  }

  return (
    <Draggable
      key={`page-${pageIndex}`}
      draggableId={`page-${pageIndex}`}
      index={pageIndex}
      isDragDisabled={readOnly}
    >
      {(provided) => (
        <Stack ref={provided.innerRef} {...provided.draggableProps}>
          <Stack
            direction={"horizontal"}
            gap={"lg"}
            className={twMerge("group", page.sections.length > 0 ? "pb-(--gap-xl)" : undefined)}
          >
            {renderEditBlock(pageIndex, undefined, undefined, provided.dragHandleProps)}
            <Stack className={"flex-1"}>
              <Title size={"xs"} header={t("common:page")} />
              <Title
                header={page.title || t("common:no_title")}
                size={"xl"}
                className={page.title ? "" : "text-secondary"}
              />
            </Stack>
          </Stack>

          <HighlightDroppable droppableId={`sections-${pageIndex}`} type="section">
            {(provided) => (
              <Stack gap={"lg"} {...provided.droppableProps} ref={provided.innerRef}>
                {page.sections.map((_, sectionIndex) => (
                  <Fragment key={`section-${pageIndex}-${sectionIndex}`}>
                    <FormSectionComponent pageIndex={pageIndex} sectionIndex={sectionIndex} />
                    {page.sections.length > 1 && <hr className="ml-34" />}
                  </Fragment>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </HighlightDroppable>
        </Stack>
      )}
    </Draggable>
  );
};
